
import { useState } from 'react';

interface QRCodeSectionProps {
  modelId: number;
  modelName: string;
}

export default function QRCodeSection({ modelId, modelName }: QRCodeSectionProps) {
  const [showQRCode, setShowQRCode] = useState(false);
  const modelUrl = `${window.location.origin}/model/${modelId}`;

  const generateQRCode = () => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(modelUrl)}`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(modelUrl);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  return (
    <section className="py-16 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl mb-16">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Quick Access & Reordering</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Generate a QR code for instant access to this model's page. Perfect for easy reordering 
              or sharing with friends and colleagues.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            {/* QR Code Display */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              {showQRCode ? (
                <div className="space-y-4">
                  <img
                    src={generateQRCode()}
                    alt={`QR Code for ${modelName}`}
                    className="w-48 h-48 mx-auto border border-gray-200 rounded-lg"
                  />
                  <div className="text-sm text-gray-600">
                    <p className="font-medium mb-2">Scan to access:</p>
                    <p className="break-all bg-gray-50 p-2 rounded text-xs">{modelUrl}</p>
                  </div>
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={copyToClipboard}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-file-copy-line"></i>
                      Copy URL
                    </button>
                    <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-download-line"></i>
                      Download QR
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-48 h-48 mx-auto bg-gray-100 rounded-lg flex items-center justify-center">
                    <i className="ri-qr-code-line text-6xl text-gray-400"></i>
                  </div>
                  <p className="text-gray-600">Click below to generate QR code</p>
                </div>
              )}
            </div>

            {/* Features and Benefits */}
            <div className="space-y-6">
              <div className="text-left">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">QR Code Benefits</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-full flex-shrink-0">
                      <i className="ri-smartphone-line text-xl text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Instant Mobile Access</h4>
                      <p className="text-gray-600 text-sm">Scan with any smartphone camera to instantly access this model's page</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
                      <i className="ri-refresh-line text-xl text-green-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Easy Reordering</h4>
                      <p className="text-gray-600 text-sm">Keep the QR code for quick reorders without searching</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-full flex-shrink-0">
                      <i className="ri-share-line text-xl text-purple-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Share with Others</h4>
                      <p className="text-gray-600 text-sm">Perfect for sharing model details with friends or colleagues</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-full flex-shrink-0">
                      <i className="ri-bookmark-line text-xl text-orange-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Save for Later</h4>
                      <p className="text-gray-600 text-sm">Bookmark this model for future reference or comparison</p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowQRCode(!showQRCode)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                {showQRCode ? 'Hide QR Code' : 'Generate QR Code'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}